import { Checkbox, FormControlLabel } from "@mui/material";
import { PdfTemplateType } from "../../../../pdf-templates/document-template.types";
import { FileText, Package, Receipt, Calendar, Copy, FileDown, CreditCard, Banknote, FileCheck, Bell, AlertTriangle, AlertOctagon, ClipboardList } from "lucide-react";

interface PdfTypeSelectorProps {
	selectedTypes: PdfTemplateType[];
	onSelectionChange: (types: PdfTemplateType[]) => void;
}

const PDF_TYPES = [
	{
		type: PdfTemplateType.KOSTENVORANSCHLAG,
		label: "Kostenvoranschlag",
		description: "Quotation",
		icon: <FileText className="w-5 h-5 text-blue-600" />,
	},
	{
		type: PdfTemplateType.LIEFERSCHEIN,
		label: "Lieferschein",
		description: "Delivery Note",
		icon: <Package className="w-5 h-5 text-green-600" />,
	},
	{
		type: PdfTemplateType.RECHNUNG,
		label: "Rechnung",
		description: "Invoice",
		icon: <Receipt className="w-5 h-5 text-orange-600" />,
	},
	{
		type: PdfTemplateType.SAMMELRECHNUNG,
		label: "Sammelrechnung",
		description: "Monthly Invoice",
		icon: <Calendar className="w-5 h-5 text-purple-600" />,
	},
	{
		type: PdfTemplateType.RECHNUNGSDOPPEL,
		label: "Rechnungsdoppel",
		description: "Duplicate Invoice",
		icon: <Copy className="w-5 h-5 text-amber-600" />,
	},
	{
		type: PdfTemplateType.GUTSCHRIFT,
		label: "Gutschrift",
		description: "Credit Note",
		icon: <FileDown className="w-5 h-5 text-emerald-600" />,
	},
	{
		type: PdfTemplateType.KARTENZAHLUNGSQUITTUNG,
		label: "Kartenzahlungsquittung",
		description: "Card Payment Receipt",
		icon: <CreditCard className="w-5 h-5 text-indigo-600" />,
	},
	{
		type: PdfTemplateType.BARZAHLUNGSQUITTUNG,
		label: "Barzahlungsquittung",
		description: "Cash Payment Receipt",
		icon: <Banknote className="w-5 h-5 text-teal-600" />,
	},
	{
		type: PdfTemplateType.AKONTORECHNUNG,
		label: "Akontorechnung",
		description: "Advance Payment Invoice",
		icon: <FileCheck className="w-5 h-5 text-cyan-600" />,
	},
	{
		type: PdfTemplateType.ZAHLUNGSERINNERUNG,
		label: "Zahlungserinnerung",
		description: "Payment Reminder",
		icon: <Bell className="w-5 h-5 text-yellow-600" />,
	},
	{
		type: PdfTemplateType.ERSTE_MAHNUNG,
		label: "Erste Mahnung",
		description: "First Notice",
		icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
	},
	{
		type: PdfTemplateType.FOLGEMAHNUNG,
		label: "Folgemahnung",
		description: "Follow-up Notice",
		icon: <AlertOctagon className="w-5 h-5 text-red-600" />,
	},
	{
		type: PdfTemplateType.LETZTE_MAHNUNG,
		label: "Letzte Mahnung",
		description: "Final Notice",
		icon: <AlertOctagon className="w-5 h-5 text-red-800" />,
	},
	{
		type: PdfTemplateType.AUFTRAGSBLATT,
		label: "Auftragsblatt",
		description: "Order Sheet",
		icon: <ClipboardList className="w-5 h-5 text-slate-600" />,
	},
] as const;

export const ESSENTIAL_TYPES = [
	PdfTemplateType.KOSTENVORANSCHLAG,
	PdfTemplateType.LIEFERSCHEIN,
	PdfTemplateType.RECHNUNG,
	PdfTemplateType.SAMMELRECHNUNG,
];

export const ALL_PDF_TYPES = PDF_TYPES.map(pt => pt.type);

export const PdfTypeSelector: React.FC<PdfTypeSelectorProps> = ({
	selectedTypes,
	onSelectionChange,
}) => {
	const handleToggle = (type: PdfTemplateType) => {
		const isSelected = selectedTypes.includes(type);

		if (isSelected) {
			onSelectionChange(selectedTypes.filter(t => t !== type));
		} else {
			onSelectionChange([...selectedTypes, type]);
		}
	};

	const handleSelectAll = () => {
		onSelectionChange(PDF_TYPES.map(pt => pt.type));
	};

	const handleSelectEssentials = () => {
		onSelectionChange(ESSENTIAL_TYPES);
	};

	const handleClearAll = () => {
		onSelectionChange([]);
	};

	return (
		<div className="bg-white border border-gray-200 rounded-lg p-6">
			<div className="flex items-center justify-between mb-4">
				<h2 className="text-lg font-semibold text-gray-900">
					PDF-Typen auswählen ({selectedTypes.length}/{PDF_TYPES.length})
				</h2>
				<div className="flex gap-2">
					<button
						onClick={handleSelectEssentials}
						className="text-sm text-blue-600 hover:text-blue-700 font-medium"
						disabled={selectedTypes.length === ESSENTIAL_TYPES.length && ESSENTIAL_TYPES.every(t => selectedTypes.includes(t))}
					>
						Essentials
					</button>
					<span className="text-gray-300">|</span>
					<button
						onClick={handleSelectAll}
						className="text-sm text-blue-600 hover:text-blue-700 font-medium"
						disabled={selectedTypes.length === PDF_TYPES.length}
					>
						Alle
					</button>
					<span className="text-gray-300">|</span>
					<button
						onClick={handleClearAll}
						className="text-sm text-blue-600 hover:text-blue-700 font-medium"
						disabled={selectedTypes.length === 0}
					>
						Keine
					</button>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
				{PDF_TYPES.map((pdfType) => {
					const isSelected = selectedTypes.includes(pdfType.type);

					return (
						<div
							key={pdfType.type}
							className={`border rounded-lg p-4 cursor-pointer transition-colors ${
								isSelected
									? 'border-blue-500 bg-blue-50'
									: 'border-gray-200 hover:bg-gray-50'
							}`}
							onClick={() => handleToggle(pdfType.type)}
						>
							<div className="flex items-start gap-3">
								<Checkbox
									checked={isSelected}
									onChange={() => handleToggle(pdfType.type)}
									onClick={(e) => e.stopPropagation()}
								/>
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										{pdfType.icon}
										<h3 className="font-medium text-gray-900">
											{pdfType.label}
										</h3>
									</div>
									<p className="text-sm text-gray-600">
										{pdfType.description}
									</p>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{selectedTypes.length === 0 && (
				<div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
					Bitte wählen Sie mindestens einen PDF-Typ aus.
				</div>
			)}
		</div>
	);
};
